import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const ReceiptPage: React.FC = () => {
  useDocumentTitle('Payment Receipt');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) fetchReceipt();
  }, [id]);

  const fetchReceipt = async () => {
    try {
      const response = await subscriptionAPI.getReceipt(parseInt(id!));
      setReceipt(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow && receipt?.html) {
      printWindow.document.write(receipt.html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  if (loading) {
    return <DashboardLayout><div className="d-flex justify-content-center py-5"><LoadingSpinner /></div></DashboardLayout>;
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="container-fluid text-center py-5">
          <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '3rem' }}></i>
          <h4 className="mt-3">{error}</h4>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/billing')}>Back to Billing</button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <button className="btn btn-link text-decoration-none p-0 mb-2" onClick={() => navigate('/billing/payments')}>
              <i className="bi bi-arrow-left me-2"></i>Back to Payments
            </button>
            <h2 className="h3 fw-bold mb-0">Receipt {receipt?.receipt_number}</h2>
          </div>
          <button className="btn btn-outline-primary" onClick={handlePrint}>
            <i className="bi bi-printer me-2"></i>Print Receipt
          </button>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            {receipt?.html ? (
              <div dangerouslySetInnerHTML={{ __html: receipt.html }} />
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">Receipt content not available</p>
                {receipt?.payment && (
                  <div>
                    <p><strong>Payment ID:</strong> {receipt.payment.id}</p>
                    <p><strong>Amount:</strong> {receipt.payment.currency} {Number(receipt.payment.amount || 0).toFixed(2)}</p>
                    <p><strong>Plan:</strong> {receipt.payment.plan_name}</p>
                    <p><strong>Date:</strong> {receipt.payment.paid_at ? new Date(receipt.payment.paid_at).toLocaleDateString() : '-'}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ReceiptPage;
